import {
    OnInit,
    Component,
    Inject,
    Injector,
    OnDestroy,
    ViewChild,
    TemplateRef,
    Input,
    Output,
    EventEmitter
} from '@angular/core';

import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { TabComponent } from '../../../Layout/components/Tab/tab.component';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { ChipModel } from '../../../Forms/models/chip.model';
import { TwitterAvatarSize } from '../../../Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { TargetingResultModel } from '../../../../Discover/Targeting/models/targeting-result.model';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { TargetingSearchService } from '../../../../Discover/Targeting/services/targeting-search.service';
import { ListPickerDialogComponent } from '../ListPickerDialog/list-picker-dialog.component';
import { ListPickerService } from '../../services/list-picker.service';
import { StaffSearchService } from '../../services/staff-search.service';
import { RightSidebarComponent } from '../../../Layout/components/RightSidebar/right-sidebar.component';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { FilterGroupModel } from '../../../Filter/models/filter-group.model';
import { TargetingListList } from '../../../../Discover/Targeting/models/targeting-list-list.model';
import { AutocompleteCustomOption, AppearCondition } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { ChipContainerComponent } from '../../../Forms/components/ChipContainer/chip-container.component';
import { AutocompleteComponent } from '../../../Forms/components/Autocomplete/autocomplete.component';
import { PrivateDataModel } from '../../../../Discover/Targeting/models/private-data.model';
import { TargetingRhpContextsService } from '../../../TargetingShared/services/targeting-rhp-contexts.service';
import { CommonApiErrorEnum } from '../../../../api/genericApi/enums/common-api-error.enum';
import { TargetingList } from '../../../../Discover/Targeting/models/targeting-list.model';
import { animate, AnimationEvent, keyframes, style, transition, trigger } from '@angular/animations';
import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { toolbarAnimation } from '@Common/Misc/animations/toolbar-animation';
import { ToolbarStateService } from '@Common/TargetingShared/services/toolbar-state.service';



@Component({
    selector: 'agpr-staff-search-tab',
    templateUrl: './staff-search-tab.component.html',
    styleUrls: [
        './staff-search-tab.component.scss',
        '../../../TargetingShared/components/EntityDetails/entity-details.component.scss', // Header card styling
        '../../../../Discover/Targeting/components/TargetingResults/targeting-results.component.scss' // List picker styling
    ],
    providers: [
        ToolbarStateService,
        StaffSearchService,
        {
            provide: TabComponent,
            useExisting: StaffSearchTabComponent
        },
        {
            provide: 'PaginatorServiceInterface',
            useExisting: StaffSearchService
        },
        {
            provide: FilterService,
            useExisting: StaffSearchService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: StaffSearchService
        },
        {
            provide: 'StaffSearchListPickerService',
            useClass: ListPickerService
        },
        {
            provide: ListPickerService,
            useExisting: 'StaffSearchListPickerService'
        }
    ],
    animations: [
        trigger('fade', [
            transition('* => true', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 0
                    }),
                    style({
                        opacity: 1
                    })
                ]))
            ]),
            transition('true => *', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 1
                    }),
                    style({
                        opacity: 0
                    })
                ]))
            ])
        ]),
        toolbarAnimation
    ],
    standalone: false
})

export class StaffSearchTabComponent extends TabComponent implements OnInit, OnDestroy {

    @Input() isActive: boolean = true;
    @Input() hideBusinessCard: boolean = false;

    @ViewChild('tabHeader') declare _header: TemplateRef<any>;
    public get header(): TemplateRef<any> {
        return this._header;
    }
    public set header(value: TemplateRef<any>) {
        this._header = value;
    }
    @ViewChild('listChipsComponent') public listChipsComponent: ChipContainerComponent;
    @ViewChild('listBuilderDropdown') public listBuilderDropdown: AutocompleteComponent;

    @Output() showRhpTakeOver?: EventEmitter<any> = new EventEmitter<any>();

    private subscriptions: Subscription[] = [];

    id: string = 'staffSearchTab';

    // page results, excluding primary contact
    results: TargetingResultModel[];

    // number of results per page (includes primary contact when on first page)
    numResults: number = 0;

    // sum of all page results
    total: number;

    selectedResults: TargetingResultModel[] = [];

    selectAll: boolean = false;
    selectAllPage: boolean = false;

    pageNumber: number = 1;
    primaryContact: TargetingResultModel;
    selectedOutlet: OutletModel;
    contactPrivateData: PrivateDataModel;
    outletPrivateData: PrivateDataModel;

    targetingLists: TargetingListList[];
    selectedListsChips: ChipModel[] = [];
    selectedLists: TargetingListList[] = [];
    targetingLists$: Observable<TargetingListList[]>;
    freshCreatedId: number;
    workingListsLocked: boolean = false;

    loading = false;
    chipsVisible = false;
    sidebarExpanded: boolean = false;
    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    readonly listBuilderOptions: AutocompleteCustomOption[] = [{
        label: 'Discover.listBuilder.createList',
        action: (listName: string) => {
            if (listName.length === 0) {

                return;
            }

            this.targetingListService.isNameDuplicated$(listName).pipe(
                switchMap((duplicate) => {
                    if (duplicate) {
                        throw new Error(CommonApiErrorEnum.duplicateName);
                    } else {
                        const list = new TargetingList({name: listName});
                        return this.targetingListService.create(list);
                    }
                })
            ).subscribe({
                next: (id) => {
                    this.freshCreatedId = id;
                    this.newlyAddedList();
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.dialogs.newList.duplicate',
                    });
                }
            });
        },
        appearCondition: AppearCondition.noExactMatchInSuggestions,
        clearOnSelect: false
    }];

    constructor(
        public staffSearchService: StaffSearchService,
        public targetingSearchService: TargetingSearchService,
        public coreDialogService: CoreDialogService,
        public targetingListService: TargetingListService,
        public rhp: RightSidebarComponent,
        public targetingRhpContextsService: TargetingRhpContextsService,
        private messageService: MessageService,
        public injector: Injector,
        private userPreferencesService: UserPreferencesService,
        private toolbarStateService: ToolbarStateService,
        @Inject('StaffSearchListPickerService') public listPickerService: ListPickerService
    ) {
        super();
        this.targetingLists$ = this.targetingListService.all$;
        this.listPickerService.useLocking = true;
    }

    ngOnInit() {
        this.subscriptions.push(
            this.rhp.selectedTabIndexChange.subscribe(() => this.selectNone()),
            combineLatest([
                this.staffSearchService.getResults(),
                this.staffSearchService.getPrimaryContact()
            ]).subscribe(([contacts, primaryContact]) => {
                this.primaryContact = primaryContact;
                this.results = contacts;

                // remove the primaryContact from results, since we show it separately
                this.results = this.results?.filter(contact => contact?.id !== this.primaryContact?.id);
                this.numResults = this.results?.length;

                // update number of results to include primaryContact when visible
                if (this.primaryContact && this.pageNumber === 1 && !this.chipsVisible) {
                    this.numResults++;
                }
            }),
            this.staffSearchService.getFilters().subscribe(filters => {
                this.chipsVisible = !!filters.find(chipFilter => {
                    if (chipFilter instanceof FilterGroupModel) {
                        return chipFilter.children.find(x => x.chips && x.chips.length > 0);
                    } else {
                        return chipFilter.chips && chipFilter.chips.length > 0;
                    }
                });

                if (this.chipsVisible) {
                    this.selectNone();
                }
            }),
            this.staffSearchService.getSearchLoading().subscribe(loading => this.loading = loading),
            this.targetingRhpContextsService.getOutlet().subscribe(outlet => {
                this.selectedOutlet = outlet.outlet;
                this.selectNone();
            }),
            this.targetingRhpContextsService.getContactPrivateData().pipe(filter(x => !!x)).subscribe(privateData => {
                this.contactPrivateData = privateData;
            }),
            this.targetingRhpContextsService.getOutletPrivateData().pipe(filter(x => !!x)).subscribe(privateData => {
                this.outletPrivateData = privateData;
            }),
            this.staffSearchService.getItemCount().subscribe(total => this.total = total),
            this.staffSearchService.getPageCriteria().subscribe((pageCriteria) => {
                this.pageNumber = pageCriteria?.page;
                this.selectNone();
            }),
            this.listPickerService.getWorkingLists().pipe(
                filter(x => !!x))
                .subscribe(lists => {
                    this.selectedLists = lists;
                    this.selectedListsChips = lists.map(list => ({
                        label: list.name
                    }));
                }),
            this.listPickerService.getWorkingListsLock().subscribe(lock => {
                this.workingListsLocked = lock;
            }),
            this.targetingListService.all$.subscribe(
                (lists) => {
                    this.targetingLists = lists;
                    // remove lists that have been deleted
                    this.selectedLists = this.selectedLists.filter((selectedList) => {
                        return !!this.targetingLists.find(list => selectedList.id === list.id);
                    });
                    this.newlyAddedList();
                }
            ),
            this.userPreferencesService.getUserAppPreferences().subscribe(prefs => {
                this.toolbarStateService.toolbarAnimationDisabled = !prefs.generalPrefs.accessibility.collapseToolbars;
            })
        );
    }

    newlyAddedList() {
        if (this.freshCreatedId) {
            const freshCreatedList = this.targetingLists.find(x => x.id === this.freshCreatedId);
            this.listPickerService.addWorkingList(freshCreatedList);
            this.listPickerService.setWorkingListsLock(true);
            this.freshCreatedId = undefined;
        }
    }

    updateListBuilder() {
        this.listPickerService.setWorkingLists(this.selectedLists);
        this.selectedListsChips = this.selectedLists.map(workingList => ({
            label: workingList.name
        }));

        this.listPickerService.setWorkingListsLock(true);
    }

    openListPicker(addMode: boolean = true) {
        this.coreDialogService.open(ListPickerDialogComponent, {
            inputs: {
                addMode: addMode,
                outletUuid: this.selectedOutlet.id
            },
            outputs: {}
        },
        this.injector);
    }

    addToList() {
        this.listPickerService.setSelectedEntities(this.selectedResults);
        if (this.selectedListsChips?.length > 0) {
            this.listPickerService.addSelectedEntitiesToWorkingLists(this.selectedOutlet.id);
        } else {
            this.openListPicker();
        }
    }

    clearListChips() {
        this.listChipsComponent.chipPopover.hide();
        this.listPickerService.clearWorkingLists();
    }

    toggleSelectAllPage() {
        if (this.selectAllPage) {
            this.selectAllInPage();
        } else {
            this.selectNone();
        }
    }

    selectNone(): void {
        this.listPickerService.setAllResultsSelected(false);
        this.selectAll = false;
        this.selectAllPage = false;
        this.selectedResults = [];
    }

    selectAllInPage(): void {
        this.selectedResults = [...this.results];

        // add proper primary contact if no filters and on page 1
        if (this.primaryContact && !this.chipsVisible && this.pageNumber === 1) {
            this.selectedResults.push(this.primaryContact);
        }
    }

    selectAllResults() {
        this.listPickerService.setAllResultsSelected(true, this.total);
        this.selectAll = true;
    }

    onCardSelect(results: TargetingResultModel[]) {
        this.selectedResults = results;
        this.selectAllPage = (this.numResults > 0 && this.selectedResults?.length >= this.numResults);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.staffSearchService.registerPagingValidator(null);
    }

    /**
     * Delegates scroll updates to the ToolbarStateService, which handles
     * collapsing or expanding the toolbar based on scroll movement.
     *
     * @param scrollTop - The current vertical scroll position of the container.
     */
    handleScroll(scrollTop: number) {
        this.toolbarStateService.onScroll(scrollTop);
    }

    /**
     * Triggered when the toolbar animation starts.
     */
    onAnimationStart(event: AnimationEvent) {
        this.toolbarStateService.onAnimationStart(event);
    }

    /**
     * Triggered when the toolbar animation ends.
     */
    onAnimationDone(event: AnimationEvent) {
        this.toolbarStateService.onAnimationDone(event);
    }
}
